<template>
  <SingleExpertise>
    <template #promo>
      <Promo
        v-if="postId.length"
        :id="postId"
        :type="PromoType.Publication"
        :images="promoImages"
        :title="promoTitle"
        :date-start="promoDateStart"
      >
        <template #before_content>
          <Announcement />
        </template>
      </Promo>
    </template>
    <template #aside-left>
      <Tags
        v-if="postData?.['TAGS']?.length"
        :list="postData?.['TAGS']"
        :path="postData?.['TAG_PATH']"
      />
      <Experts
        v-if="authorList.length"
        :list="authorList"
        :author-props="authorProperties"
      />
    </template>
    <template #article>
      <CmsContent
        v-if="hasPreviewImage"
        class="post-block html-editor -сontent"
        :html="previewText"
        :image-renderer="imageRenderer"
      />
      <CmsContent
        v-if="detailText.length"
        class="post-block html-editor -сontent -header-spacing"
        :html="detailText"
        :image-renderer="imageRenderer"
        :shortcodes="shortcodes"
        :elements="chartElements"
      />
      <div v-if="documentList.length" class="post-block -report -header-spacing">
        <DocumentList :list="documentList" />
      </div>
    </template>
    <template #after-article>
      <div
        v-if="showInfoBanner && !(showForm && formSid === 'pers_consultation')"
        class="post-block -form"
      >
        <FormPersConsultation
          class="g-start-ld-4 g-col-ld-6 g-col-full"
          sid="pers_consultation"
          theme="light"
          @after-submit="handleFormService"
        />
      </div>
      <div v-if="formSid.length && showForm" class="post-block -form">
        <FormPersConsultation
          class="g-start-ld-4 g-col-ld-6 g-col-full"
          :sid="formSid"
          :title="formTitle"
          :description="formDescription"
          @after-submit="handleFormService"
        />
      </div>
    </template>
    <template #aside-right>
      <InterestingPosts :list="newsList" />
    </template>
  </SingleExpertise>

  <!-- Служебное модальное окно -->
  <Modal
    v-if="isServiceOpen"
    view="classic"
    width="md"
    :title="modal.title"
    :overlay="true"
    theme="light"
    @on-close="() => modals.toggle('service', false)"
  >
    <p
      v-if="modal.description.length"
      class="dialog-qualified-investor__desc t1"
      v-html="modal.description"
    />
  </Modal>
</template>

<script setup lang="ts">
  /*=== Import ===*/
  import { useRoute, reactive, computed, createError } from '#imports';
  import { defineAsyncComponent, hydrateOnVisible } from 'vue';
  import { Promo, PromoType } from '@nuxt-widgets/promo';
  import {
    SingleExpertise,
    Tags,
    Experts,
    DocumentList,
  } from '@nuxt-widgets/single-expertise';
  import { Announcement } from '@nuxt-widgets/announcement';
  import {
    AudioPlayer,
    createAudioShortcode,
    parseAudioRecords,
  } from '@nuxt-widgets/audio-player';
  import { useAnnouncement } from '@nuxt-widgets/announcement/api';
  import { useFooterDisclaimer } from '@nuxt-shared/composables';
  import {
    useJsonLd,
    article,
    videoObjectsFromHtml,
  } from '@nuxt-shared/composables/useJsonLd';
  import { CmsContent } from '@nuxt-shared/ui/cms-content';
  import { ANALYTICS_SUBSCRIPTION_SID } from '@nuxt-widgets/analytics-subscription';
  import { chartElements } from '@nuxt-widgets/chart-widget';
  import { Modal } from '@nuxt-shared/ui/modal';
  import { useBackendAPI } from '@nuxt-shared/composables/useBackendAPI';
  import { useModalStore } from '@nuxt-shared/composables/stores/useModalStore';
  import {
    PublicationImage,
    createPublicationImageRenderer,
    hasPublicationImage,
  } from '@nuxt-widgets/publication-image';
  import {
    PublicationAccordion,
    createAccordionShortcode,
    parseAccordionItems,
    type AccordionComponentData,
  } from '@nuxt-widgets/publication-accordion';

  // Ленивая гидрация блоков ниже фолда: SSR-HTML виден сразу, а JS-гидрация
  // откладывается до появления блока во вьюпорте — меньше работы главного
  // потока на старте страницы (INP/TBT).
  const FormPersConsultation = defineAsyncComponent({
    loader: () => import('@nuxt-features/form-pers-consultation'),
    hydrate: hydrateOnVisible(),
  });
  const InterestingPosts = defineAsyncComponent({
    loader: () =>
      import('@nuxt-widgets/single-expertise').then((m) => m.InterestingPosts),
    hydrate: hydrateOnVisible(),
  });

  /*=== Route ===*/
  const route = useRoute();
  const singleCode = route.params.id as string;
  // Определяем раздел из URL: /news/xxx → news, /ideas/xxx → ideas и т.д.
  const section = route.path.split('/').filter(Boolean)[0] || 'news';

  /*=== Fetch ===*/
  const [postRes, newsRes] = await Promise.all([
    useBackendAPI(`/${section}/${singleCode}/`),
    useBackendAPI('expertise/interesting_news/?countElements=5'),
    // Префетч анонса: виджет Announcement вложен в Promo и без префетча
    // ждал бы ответа баннера; ключ совпадает — повторного HTTP не будет.
    useAnnouncement(),
  ]);

  const { data: postData, status: postStatus } = postRes;

  const hasData = computed(
    () => Object.keys(postData?.value || {}).length && postStatus.value === 'success',
  );

  const postId = computed(() => postData?.value?.['ID'] ?? '');
  const postProps = computed(() => postData?.value?.['PROPERTIES'] || {});

  // Данные баннера из уже полученного поста: картинки, заголовок и дата
  // совпадают с ответом banner/publication/{id} — с ними hasAllProps в Promo
  // становится true и дублирующий запрос usePromo не выполняется.
  const promoImages = computed(() => postData?.value?.['IMAGE'] || {});
  const promoTitle = computed(() => postData?.value?.['NAME'] || '');
  // Битрикс отдаёт `ACTIVE_FROM` как `dd.mm.yyyy hh:mm:ss`; Promo передаёт
  // строку в общий useDateFormat без потери даты через Number(...).
  const promoDateStart = computed(() => postData?.value?.['ACTIVE_FROM'] || 0);

  // Дисклеймер выставляем сразу после await — до любых других асинхронных
  // операций, иначе на SSR футер успевает отрендериться раньше, чем дисклеймер
  // попадёт в стор, и в подвале он не появляется. См. webinars/[id].vue.
  useFooterDisclaimer(() => postProps.value?.['AGREEMENT']?.['ELEMENT']?.[0]);

  const { data: newsData } = newsRes;

  // Исключаем текущую публикацию из подборки (раньше это делал бэк
  // по publicationId) и режем до 4 элементов.
  const normalizePath = (path: string) => path.replace(/\/+$/, '');
  const currentPath = normalizePath(route.path);
  const newsList = computed(() => {
    const items = Array.isArray(newsData.value) ? newsData.value : [];
    return items
      .filter((item) => normalizePath(item?.link || '') !== currentPath)
      .slice(0, 4);
  });

  // Нет данных — единая страница ошибки app/error.vue (сама выставит
  // HTTP-статус 404 и светлый header).
  if (!hasData.value) {
    throw createError({ statusCode: 404, fatal: true });
  }

  // state
  const modal = reactive({
    title: '',
    description: '',
  });

  // computed
  const authorList = computed(() => postData?.value?.['AUTHOR'] || []);
  const authorProperties = computed(
    () => postProps?.value?.['AUTHOR']?.['ELEMENT'] || [],
  );
  const previewText = computed(() => postData?.value?.['PREVIEW_TEXT'] || '');
  const hasPreviewImage = computed(() => hasPublicationImage(previewText.value));
  const detailText = computed(() => {
    const detail = postData?.value?.['DETAIL_TEXT'] || '';
    const previewFallback = hasPreviewImage.value ? '' : previewText.value;
    return removeHashTags(detail || previewFallback);
  });
  const imageRenderer = createPublicationImageRenderer(PublicationImage);

  // Шорткоды детального текста: `#<имя>#` / `#<имя>_<параметры>#` редактор
  // ставит прямо в «Детальном описании», `CmsContent` меняет их на компоненты.
  // Новый модуль (галерея, картинка) — ещё одна строка этой карты.
  //
  // Аккордеон приходит нормализованным блоком `COMPONENTS.accordion`; старые
  // свойства оставлены fallback'ом для прежних ответов API. Шорткод выбирает,
  // какие элементы показать:
  // `#accordion#` — все, `#accordion_1#`, `#accordion_2-5#`, `#accordion_3,5#`.
  const accordionComponent = computed<AccordionComponentData | null>(
    () => postData?.value?.['COMPONENTS']?.['accordion'] ?? null,
  );
  const accordionTitle = computed(
    () =>
      accordionComponent.value?.['title'] ||
      postProps?.value?.['C_ACCORDION_TITLE']?.['VALUE'] ||
      '',
  );
  const accordionItems = computed(() =>
    parseAccordionItems(
      accordionComponent.value?.['items'] ?? postProps?.value?.['C_ACCORDION'],
    ),
  );
  // Аудиозаписи приходят блоком `COMPONENTS.audio`; шорткод выбирает, какие
  // показать: `#audio#` — все, `#audio_1#`, `#audio_2-5#`, `#audio_3,5#`.
  const audioRecords = computed(() =>
    parseAudioRecords(postData?.value?.['COMPONENTS']?.['audio'], promoTitle.value),
  );
  const shortcodes = {
    accordion: createAccordionShortcode(
      accordionItems,
      PublicationAccordion,
      accordionTitle,
    ),
    audio: createAudioShortcode(audioRecords, AudioPlayer),
  };

  // Форма
  const formSid = computed(
    () =>
      postProps?.value?.['FORM_CONNECT']?.['VALUE'] ||
      'subscribe_analytics_non_qualified',
  );
  const formTitle = computed(() => postProps?.value?.['FORM_NAME']?.['VALUE'] || '');
  const formDescription = computed(() => {
    const desc = postProps?.value?.['FORM_DESCRIPTION']?.['VALUE'];
    if (!desc) return '';
    return typeof desc === 'object' ? desc['TEXT'] || '' : desc;
  });
  const showForm = computed(
    () =>
      postProps?.value?.['SHOW_FORM']?.['VALUE'] === 'Y' &&
      formSid.value !== ANALYTICS_SUBSCRIPTION_SID,
  );
  const showInfoBanner = computed(
    () => postProps?.value?.['SHOW_INFO_BANNER']?.['VALUE'] === 'Y',
  );

  const documentList = computed(() => {
    const result = [];

    // Добавляем новые материалы
    const newFileList = postProps?.value?.['MATERIAL_FILE']?.['FILES'] || [];
    const newFileNames = postProps?.value?.['MATERIAL_NAME']?.['VALUE'] || [];
    const newFileSecureOpts = postProps?.value?.['MATERIAL_SECURE']?.['VALUE'] || [];

    if (newFileList.length) {
      for (const [index, value] of newFileList.entries()) {
        const type = value['PATH']?.split('.')?.at(-1) || '';
        result.push({
          id: value?.['ID'] || '',
          name: newFileNames?.[index] || '',
          size: ' ' + value['FILE_SIZE'],
          path: value['PATH'],
          type: type.toUpperCase(),
          secure: newFileSecureOpts?.[index] === 'Y' || false,
        });
      }
    }

    // Добавляем старые материалы
    const oldPdfFiles = {
      file1: {
        value: postProps?.value?.['PDF']?.['FILES'] || [],
        name: postProps?.value?.['FN_PDF']?.['VALUE'] || '',
        secure: postProps?.value?.['SECURE_PDF']?.['VALUE'] === 'Y' || false,
      },
      file2: {
        value: postProps?.value?.['DOC_PDF']?.['FILES'] || [],
        name: postProps?.value?.['FN_DOC_PDF']?.['VALUE'] || '',
        secure: postProps?.value?.['SECURE_DOC_PDF']?.['VALUE'] === 'Y' || false,
      },
    };

    for (const key in oldPdfFiles) {
      const oldFileObj = oldPdfFiles[key];
      if (oldFileObj?.value?.length) {
        const oldFilePath = oldFileObj.value?.[0]?.['PATH'] || '';
        const oldFileType = oldFilePath?.split('.')?.at(-1) || '';

        result.push({
          id: oldFileObj.value?.[0]?.['ID'] || '',
          name: oldFileObj?.['name'] || '',
          size: ' ' + oldFileObj.value?.[0]?.['FILE_SIZE'] || '',
          path: oldFilePath,
          type: oldFileType.toUpperCase(),
          secure: oldFileObj?.['secure'] || false,
        });
      }
    }

    return result;
  });

  /*=== Head ===*/
  // Микроразметка Article (schema.org). Данные те же, что рисует страница:
  // заголовок и картинка — из промо-блока, авторы — из блока Experts.
  useJsonLd(() => {
    const nodes = [];

    const articleNode = article({
      url: route.path,
      headline: promoTitle.value,
      description: postData?.value?.['PREVIEW_TEXT'] || '',
      datePublished: promoDateStart.value,
      dateModified: postData?.value?.['TIMESTAMP_X'] || promoDateStart.value,
      image: promoImages.value?.['IMG_LG'] || '',
      author: authorList.value.map((item: Record<string, string>) => item['NAME'] || ''),
    });

    if (articleNode) nodes.push(articleNode);

    // Плееры (подкасты и просто публикации с видео) редактор вставляет прямо
    // в текст обёрткой .video-frame с iframe — размечаем их как VideoObject.
    nodes.push(
      ...videoObjectsFromHtml(detailText.value, {
        name: promoTitle.value,
        description: postData?.value?.['PREVIEW_TEXT'] || '',
        thumbnailUrl: promoImages.value?.['IMG_LG'] || '',
        uploadDate: promoDateStart.value,
      }),
    );

    return nodes;
  });

  // Управление модальными окнами
  const modals = useModalStore(['service']);
  const isServiceOpen = computed(() => modals.status('service'));

  // methods
  const handleFormService = ({
    title,
    description,
  }: {
    title: string;
    description: string;
  }) => {
    modal.title = title;
    modal.description = description;
    modals.toggle('service', true);
  };

  function removeHashTags(str: string): string {
    // Удаляем только спец-коды компонентов вида:
    // #card#, #card_1#, #card_2-5#, #card_3,5# и аналогично для slider/gallery.
    // Коды из карты `shortcodes` здесь не трогаем — их разбирает `CmsContent`
    // и сам убирает из текста те, под которые нет данных.
    const regex = /#(?:card|slider|gallery)(?:_(?:\d+|\d+-\d+|\d+(?:,\d+)+))?#/gi;
    return str.replace(regex, '');
  }
</script>

<style lang="scss">
  @use '../scss/publication';
</style>
